import { distanceSqToAABB, type MapDef, type Vec3 } from "@rietz/shared";
import type { WorldView } from "../game/world.js";
import type { Input } from "../input/input.js";
import {
  buildCapsuleWireframe,
  buildCollisionWireframe,
  capsuleHeightForEye,
  type BuiltWireframe,
  type CapsuleWireframe,
} from "./collisionViz.js";
import { buildMarkerViz, type MarkerViz } from "./markerViz.js";
import type { DebugOverlay, OverlayExtras } from "./overlay.js";
import { buildWaypointViz, type WaypointViz } from "./waypointViz.js";

/**
 * The debug tool belt: keybinds, lifetimes, and the HUD's world block. Nothing
 * is built until its key is first pressed; a built layer is cached while the
 * map is loaded (toggling only unlinks its group) and disposed on map change;
 * `update` allocates nothing.
 *
 *   F3  collision wireframes + the local player's capsule
 *   F4  waypoint graph
 *   F5  spawn / extract / bounds markers, POI names everywhere
 *   F6  noclip — a single bit on `Input` acted on by the shared `stepMovement`
 *       on both ends; this class only flips it and never leaves it latched.
 */

export const DEBUG_HELP = [
  "keys",
  // Not a debug tool — the town map ships to players — but this printout is
  // where
  // the game tells you what the keyboard does.
  "  M   town map — POIs, extracts, your position",
  // Neither is a debug tool either, and both are here for the same reason M is.
  "  T   tactical device — the fitted lamp or laser, on/off",
  "  G   grenade — HOLD to cook, release to throw",
  "  F3  collision wireframes + player capsule",
  "  F4  waypoint graph (red node = isolated)",
  "  F5  spawns, extract names, map bounds, all POI names",
  "  F6  noclip — fly through walls; Space up, C down, Shift fast",
].join("\n");

export interface DebugToolsOptions {
  view: WorldView;
  input: Input;
  overlay: DebugOverlay;
  /** `?debug=1`: print the keybind help into the HUD for a few seconds. */
  showHelp?: boolean;
}

/** How long the boot-time help stays on screen. */
const HELP_MS = 5000;

export class DebugTools {
  private readonly view: WorldView;
  private readonly input: Input;

  private map: MapDef | null = null;

  private collision: BuiltWireframe | null = null;
  private capsule: CapsuleWireframe | null = null;
  private waypoints: WaypointViz | null = null;
  private markers: MarkerViz | null = null;

  private showCollision = false;
  private showWaypoints = false;
  private showMarkers = false;

  /** Rebuilt only when a toggle changes — the HUD reads it 5×/s. */
  private toggleText = "all off";

  /** Scratch — the HUD block is refilled in place, never reallocated. */
  private readonly extras: OverlayExtras = {
    mapId: "",
    solidCount: 0,
    propCount: 0,
    paneCount: 0,
    drawCalls: 0,
    triangles: 0,
    nearestExtractName: "",
    nearestExtractDistM: 0,
    toggles: "all off",
    noclip: false,
  };

  private disposed = false;

  constructor(opts: DebugToolsOptions) {
    this.view = opts.view;
    this.input = opts.input;

    window.addEventListener("keydown", this.onKeyDown);
    if (opts.showHelp === true) opts.overlay.showHelp(DEBUG_HELP, HELP_MS);
  }

  /** True while F6 noclip is flying the player. Read by the HUD. */
  get noclip(): boolean {
    return this.input.noclip;
  }

  /**
   * Point the tools at a map, or `null` when there is no world. Everything
   * cached belongs to the old map and is disposed here.
   */
  setMap(map: MapDef | null): void {
    if (map !== null && this.map !== null && this.map.id === map.id) {
      this.map = map;
      return;
    }
    // Noclip does NOT survive losing the world: a raid that started with the
    // bit
    // latched would begin by falling out of the map.
    if (map === null) this.input.noclip = false;
    this.disposeLayers();
    this.map = map;
    // Toggles survive a map change — if you had wireframes on, you want them
    // on when you land on the next map too. They are rebuilt on demand below.
    this.syncScene();
  }

  /**
   * One frame of debug work. `feet`/`eyeHeight` are the RENDERED values, so the
   * capsule tracks smoothing like the camera does.
   */
  update(dtMs: number, feet: Vec3, eyeHeight: number): void {
    if (this.showCollision && this.capsule !== null) {
      this.capsule.setFeet(feet.x, feet.y, feet.z);
      this.capsule.setHeight(capsuleHeightForEye(eyeHeight));
    }

    if (this.showWaypoints && this.waypoints !== null) {
      this.waypoints.update(this.view.camera.position, dtMs);
    }
  }

  /** Fills and returns the HUD's world block. Never allocates. */
  overlayExtras(playerPos: Vec3): OverlayExtras {
    const e = this.extras;
    const stats = this.view.renderStats;
    const info = this.view.renderer.info.render;

    e.mapId = stats.mapId;
    e.solidCount = stats.solidCount;
    e.propCount = stats.propCount;
    e.paneCount = stats.paneCount;
    e.drawCalls = info.calls;
    e.triangles = info.triangles;
    e.toggles = this.toggleText;
    e.noclip = this.input.noclip;

    e.nearestExtractName = "";
    e.nearestExtractDistM = 0;
    const map = this.map;
    if (map !== null) {
      let bestSq = Infinity;
      for (const ex of map.extracts) {
        const d = distanceSqToAABB(ex.zone, playerPos);
        if (d < bestSq) {
          bestSq = d;
          e.nearestExtractName = ex.name;
        }
      }
      if (bestSq < Infinity) e.nearestExtractDistM = Math.sqrt(bestSq);
    }

    return e;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener("keydown", this.onKeyDown);
    // The one toggle that outlives this object if we let it: nothing else can
    // clear the bit once the keybind is gone.
    this.input.noclip = false;
    this.disposeLayers();
  }

  // ---------------------------------------------------------------------
  // Toggles
  // ---------------------------------------------------------------------

  toggleCollision(): void {
    this.showCollision = !this.showCollision;
    this.syncScene();
  }

  toggleWaypoints(): void {
    this.showWaypoints = !this.showWaypoints;
    this.syncScene();
  }

  toggleMarkers(): void {
    this.showMarkers = !this.showMarkers;
    this.syncScene();
  }

  /**
   * Flips the noclip bit on `Input` — the whole implementation: the bit rides
   * in the next command and both ends fly the body.
   */
  toggleNoclip(): void {
    this.input.noclip = !this.input.noclip;
    this.refreshToggleText();
  }

  // ---------------------------------------------------------------------

  private readonly onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.repeat || ev.altKey || ev.ctrlKey || ev.metaKey) return;
    switch (ev.code) {
      case "F3":
        this.toggleCollision();
        break;
      case "F4":
        this.toggleWaypoints();
        break;
      case "F5":
        this.toggleMarkers();
        break;
      case "F6":
        this.toggleNoclip();
        break;
      default:
        return;
    }
    // F3 opens the find bar, F5 reloads, F6 jumps to the address bar — claimed
    // outright.
    ev.preventDefault();
  };

  /**
   * Brings the scene graph in line with the toggles, building layers on first
   * use; built layers are only linked/unlinked.
   */
  private syncScene(): void {
    const map = this.map;
    const scene = this.view.scene;

    if (this.showCollision && map !== null) {
      if (this.collision === null) this.collision = buildCollisionWireframe(map);
      if (this.capsule === null) this.capsule = buildCapsuleWireframe();
      scene.add(this.collision.group, this.capsule.group);
    } else {
      if (this.collision !== null) scene.remove(this.collision.group);
      if (this.capsule !== null) scene.remove(this.capsule.group);
    }

    if (this.showWaypoints && map !== null) {
      if (this.waypoints === null) this.waypoints = buildWaypointViz(map.waypoints);
      scene.add(this.waypoints.group);
    } else if (this.waypoints !== null) {
      scene.remove(this.waypoints.group);
    }

    if (this.showMarkers && map !== null) {
      if (this.markers === null) this.markers = buildMarkerViz(map);
      scene.add(this.markers.group);
    } else if (this.markers !== null) {
      scene.remove(this.markers.group);
    }
    // POI names are a shipped layer with a range cull (`game/poiLabels.ts`);
    // the marker toggle also lifts that cull so the whole map is surveyable.
    this.view.setPoiLabelsShowAll(this.showMarkers);

    this.refreshToggleText();
  }

  private refreshToggleText(): void {
    const parts: string[] = [];
    if (this.showCollision) parts.push("F3 collision");
    if (this.showWaypoints) parts.push("F4 waypoints");
    if (this.showMarkers) parts.push("F5 markers");
    if (this.input.noclip) parts.push("F6 noclip");
    this.toggleText = parts.length === 0 ? "all off (F3-F6)" : parts.join("  ");
  }

  private disposeLayers(): void {
    const scene = this.view.scene;
    for (const g of [
      this.collision?.group,
      this.capsule?.group,
      this.waypoints?.group,
      this.markers?.group,
    ]) {
      if (g !== undefined) scene.remove(g);
    }
    this.collision?.dispose();
    this.capsule?.dispose();
    this.waypoints?.dispose();
    this.markers?.dispose();
    this.collision = null;
    this.capsule = null;
    this.waypoints = null;
    this.markers = null;
  }
}
